/**
 * Knowledge Base index — public, unauthenticated reader at /knowledgebase.
 *
 * Hits the public API at /api/knowledgebase. Fetches categories + articles
 * in parallel on mount; debounces search and routes to /articles/search
 * when q is non-empty. When a category chip is selected, refetches the
 * article list filtered by that category's slug.
 *
 * SEO caveat: this is a CRA app — meta tags are set in useEffect, which
 * Google's crawler reads but social-share previews (FB/LinkedIn/WhatsApp)
 * do NOT. Matches the rest of the app's SEO posture; real social previews
 * would need SSR or a prerender service.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Search, X, Sparkles, MessageCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import './KnowledgeBase.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const WHATSAPP_NUMBER = '2348175658439';
const WHATSAPP_DEFAULT_TEXT = "Hi! I'm using Revstar and need help with…";

const buildWhatsAppLink = (text = WHATSAPP_DEFAULT_TEXT) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

const setMeta = (selector, attr, value) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const m = selector.match(/(name|property)="([^"]+)"/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const KnowledgeBase = () => {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null); // slug or null
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searching, setSearching] = useState(false);

  // SEO meta
  useEffect(() => {
    const title = 'Help Center — Revstar';
    const description =
      'Guides, walkthroughs, and answers for running your Revstar store. Search articles or browse by topic.';
    document.title = title;
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[name="twitter:card"]', 'content', 'summary');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch categories once
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/knowledgebase/categories`);
        const data = await res.json();
        if (data.success) setCategories(data.data || []);
      } catch {
        /* surfaced via list-load failure */
      }
    })();
  }, []);

  // Fetch articles — either filtered list, or search results
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (debouncedSearch) {
        setSearching(true);
        try {
          const params = new URLSearchParams({ q: debouncedSearch, limit: '20' });
          const res = await fetch(`${API_BASE}/api/knowledgebase/articles/search?${params}`);
          const data = await res.json();
          if (!cancelled && data.success) setArticles(data.data || []);
        } catch {
          if (!cancelled) setArticles([]);
        } finally {
          if (!cancelled) setSearching(false);
        }
        return;
      }
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: '50' });
        if (activeCategory) params.set('categorySlug', activeCategory);
        const res = await fetch(`${API_BASE}/api/knowledgebase/articles?${params}`);
        const data = await res.json();
        if (!cancelled && data.success) setArticles(data.data || []);
      } catch {
        if (!cancelled) setArticles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeCategory, debouncedSearch]);

  const featured = useMemo(
    () => (!debouncedSearch && !activeCategory ? articles.filter((a) => a.isFeatured) : []),
    [articles, debouncedSearch, activeCategory],
  );

  const rest = useMemo(() => {
    if (debouncedSearch) return articles;
    return articles.filter((a) => !a.isFeatured);
  }, [articles, debouncedSearch]);

  const isLoading = debouncedSearch ? searching : loading;

  return (
    <div className="kb-shell">
      <header className="kb-topbar">
        <div className="kb-topbar-inner">
          <Link to="/knowledgebase" className="kb-brand">
            <BookOpen size={18} />
            <span>Revstar Help<span className="kb-brand-sub">Center</span></span>
          </Link>
          <Link to="/overview" className="kb-topbar-link">
            <ArrowLeft size={14} />
            <span>Back to app</span>
          </Link>
        </div>
      </header>

      <section className="kb-hero">
        <div className="kb-hero-inner">
          <p className="kb-hero-eyebrow">Help Center</p>
          <h1 className="kb-hero-title">How can we help?</h1>
          <p className="kb-hero-subtitle">
            Browse setup guides, payment walkthroughs, and answers to the questions merchants ask most.
          </p>
          <div className="kb-search">
            <Search size={16} className="kb-search-icon" aria-hidden="true" />
            <input
              type="search"
              className="kb-search-input"
              placeholder="Search articles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            {search && (
              <button
                type="button"
                className="kb-search-clear"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </section>

      <main className="kb-main">
        <div className="kb-main-inner">
          {!debouncedSearch && categories.length > 0 && (
            <section>
              <div className="kb-chip-row">
                <button
                  type="button"
                  className={`kb-chip ${activeCategory === null ? 'is-active' : ''}`}
                  onClick={() => setActiveCategory(null)}
                >
                  All topics
                </button>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`kb-chip ${activeCategory === c.slug ? 'is-active' : ''}`}
                    onClick={() => setActiveCategory(c.slug)}
                  >
                    <span>{c.name}</span>
                    {c.articleCount > 0 && (
                      <span className="kb-chip-count">{c.articleCount}</span>
                    )}
                  </button>
                ))}
              </div>
            </section>
          )}

          {featured.length > 0 && (
            <section>
              <h2 className="kb-section-title">
                <Sparkles size={16} /> Featured
              </h2>
              <div className="kb-featured-grid">
                {featured.map((a) => (
                  <Link key={a.id} to={`/knowledgebase/${a.slug}`} className="kb-featured-card">
                    <span className="kb-featured-eyebrow">
                      {a.category?.name || 'Help'}
                    </span>
                    <h3 className="kb-featured-title">{a.title}</h3>
                    {a.excerpt && <p className="kb-featured-excerpt">{a.excerpt}</p>}
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="kb-section-title">
              {debouncedSearch
                ? `Results for "${debouncedSearch}"`
                : activeCategory
                  ? categories.find((c) => c.slug === activeCategory)?.name || 'Articles'
                  : 'All articles'}
            </h2>

            {isLoading ? (
              <div className="kb-loading">
                <RefreshCw size={14} className="kb-spin" />
                <span>Loading…</span>
              </div>
            ) : rest.length === 0 ? (
              <div className="kb-empty">
                {debouncedSearch ? (
                  <>
                    <strong>No matches.</strong> Try a different search, or message us on WhatsApp below.
                  </>
                ) : (
                  <>
                    <strong>Nothing here yet.</strong> Articles will appear as our team publishes them.
                  </>
                )}
              </div>
            ) : (
              <div className="kb-list">
                {rest.map((a) => (
                  <Link key={a.id} to={`/knowledgebase/${a.slug}`} className="kb-list-item">
                    <span className="kb-list-item-title">
                      {a.title}
                      {a.category && !activeCategory && !debouncedSearch && (
                        <span className="kb-list-item-cat">{a.category.name}</span>
                      )}
                    </span>
                    {a.excerpt && <span className="kb-list-item-excerpt">{a.excerpt}</span>}
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section className="kb-human-cta">
            <div className="kb-human-cta-text">
              <p className="kb-human-cta-title">Still stuck?</p>
              <p className="kb-human-cta-sub">Message our team on WhatsApp — we usually reply within the hour.</p>
            </div>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="kb-human-cta-btn"
            >
              <MessageCircle size={16} />
              <span>Talk to a human</span>
            </a>
          </section>
        </div>
      </main>
    </div>
  );
};

export default KnowledgeBase;
